const SECTION_SELECTOR = '[data-case-study-intro]';
const instances = new Map();
const MODAL_CONTENT_DELAY = 450;
const MODAL_CLOSE_DELAY = 350;

class CaseStudyIntroSection {
  constructor(section) {
    this.section = section;
    this.calloutNumbers = Array.from(section.querySelectorAll('[data-case-study-callout-number]'));
    this.observer = null;
    this.modal = section.querySelector('[data-case-study-modal]');
    this.modalTrigger = section.querySelector('[data-case-study-modal-trigger]');
    this.modalDialog = null;
    this.modalOverlay = null;
    this.modalClose = null;
    this.isModalOpen = false;
    this.previousActiveElement = null;
    this.openTimeout = null;
    this.closeTimeout = null;

    this.handleIntersect = this.handleIntersect.bind(this);
    this.handleModalOpen = this.handleModalOpen.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.init();
  }

  clearModalTimeouts() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  init() {
    if (this.calloutNumbers.length) {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersect, {
          threshold: 0.35,
        });
        this.calloutNumbers.forEach((el) => this.observer.observe(el));
      } else {
        this.calloutNumbers.forEach((el) => this.animateNumber(el));
      }
    }

    this.setupModal();
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.animateNumber(entry.target);
        this.observer?.unobserve(entry.target);
      }
    });
  }

  setupModal() {
    if (!this.modal || !this.modalTrigger) {
      return;
    }

    this.modalDialog = this.modal.querySelector('.case-study-modal__dialog');
    this.modalOverlay = this.modal.querySelector('[data-case-study-modal-overlay]');
    this.modalClose = this.modal.querySelector('[data-case-study-modal-close]');

    this.modalTrigger.addEventListener('click', this.handleModalOpen);
    this.modalClose?.addEventListener('click', this.handleModalClose);
    this.modalOverlay?.addEventListener('click', this.handleOverlayClick);
  }

  handleModalOpen(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (this.isModalOpen || !this.modal) {
      return;
    }

    this.previousActiveElement = document.activeElement;
    this.setDialogTransformOrigin(event);
    this.modal.classList.add('case-study-modal--visible');
    this.modal.setAttribute('aria-hidden', 'false');
    this.modalTrigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('case-study-modal-open');
    this.isModalOpen = true;
    document.addEventListener('keydown', this.handleKeyDown);
    this.modalClose?.focus();
    this.modal?.classList.remove('case-study-modal--content-visible');
    this.clearModalTimeouts();
    this.openTimeout = window.setTimeout(() => {
      this.modal?.classList.add('case-study-modal--content-visible');
      this.openTimeout = null;
    }, MODAL_CONTENT_DELAY);
  }

  handleModalClose() {
    if (!this.isModalOpen) {
      return;
    }

    this.isModalOpen = false;
    this.modal?.classList.remove('case-study-modal--content-visible');
    this.clearModalTimeouts();
    this.closeTimeout = window.setTimeout(() => {
      this.modal?.classList.remove('case-study-modal--visible');
      this.modal?.setAttribute('aria-hidden', 'true');
      this.modalTrigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('case-study-modal-open');
      document.removeEventListener('keydown', this.handleKeyDown);
      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
      this.closeTimeout = null;
    }, MODAL_CLOSE_DELAY);
  }

  handleOverlayClick(event) {
    if (event.target === this.modalOverlay) {
      this.handleModalClose();
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.handleModalClose();
    }
  }

  setDialogTransformOrigin(event) {
    if (!this.modalDialog) {
      return;
    }

    const { x, y } = this.getClickOrigin(event);
    const dialogWidth = this.modalDialog.offsetWidth;
    const dialogHeight = this.modalDialog.offsetHeight;
    const originX = (x - (window.innerWidth / 2)) + dialogWidth / 2;
    const originY = (y - (window.innerHeight / 2)) + dialogHeight / 2;
    this.modalDialog.style.transformOrigin = `${originX}px ${originY}px`;
  }

  getClickOrigin(event) {
    if (event?.clientX != null && event?.clientY != null) {
      return { x: event.clientX, y: event.clientY };
    }

    if (this.modalTrigger) {
      const rect = this.modalTrigger.getBoundingClientRect();
      if (rect) {
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      }
    }

    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };
  }

  teardownModal() {
    this.clearModalTimeouts();
    if (this.isModalOpen) {
      this.modal?.classList.remove('case-study-modal--content-visible');
      this.modal?.classList.remove('case-study-modal--visible');
      this.modal?.setAttribute('aria-hidden', 'true');
      this.modalTrigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('case-study-modal-open');
      document.removeEventListener('keydown', this.handleKeyDown);
      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
      this.isModalOpen = false;
    }
    this.modalTrigger?.removeEventListener('click', this.handleModalOpen);
    this.modalClose?.removeEventListener('click', this.handleModalClose);
    this.modalOverlay?.removeEventListener('click', this.handleOverlayClick);
  }

  animateNumber(el) {
    const parsed = this.parseNumber(el.dataset.calloutValue || el.textContent || '0');
    const start = performance.now();
    const duration = 900;

    const step = (timestamp) => {
      const progress = Math.min((timestamp - start) / duration, 1);
      const currentValue = parsed.number * progress;
      el.textContent = `${parsed.prefix}${this.formatNumber(currentValue, parsed.decimals)}${parsed.suffix}`;
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    el.textContent = `${parsed.prefix}${this.formatNumber(0, parsed.decimals)}${parsed.suffix}`;
    requestAnimationFrame(step);
  }

  parseNumber(value) {
    const rawValue = (value || '').toString().trim();
    if (!rawValue.length) {
      return { prefix: '', suffix: '', number: 0, decimals: 0 };
    }

    const match = rawValue.match(/^([^0-9]*)([0-9.,]+)(.*)$/);
    const prefix = match ? match[1] : '';
    const numericPart = match ? match[2] : '0';
    const suffix = match ? match[3] : '';
    const decimals = (numericPart.split('.')[1] || '').length;
    const number = parseFloat(numericPart.replace(/,/g, '')) || 0;
    return { prefix, suffix, number, decimals };
  }

  formatNumber(value, decimals) {
    const safeValue = Number.isFinite(value) ? value : 0;
    try {
      return Number(safeValue).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } catch (error) {
      return safeValue.toFixed(decimals);
    }
  }

  destroy() {
    if (this.observer) {
      this.calloutNumbers.forEach((el) => this.observer.unobserve(el));
      this.observer.disconnect();
      this.observer = null;
    }
    this.teardownModal();
  }
}

function initSection(section) {
  if (!section) return;
  const id = section.dataset.sectionId || section.id;
  if (instances.has(id)) {
    instances.get(id)?.destroy();
  }
  instances.set(id, new CaseStudyIntroSection(section));
}

function destroySection(sectionId) {
  if (!sectionId) return;
  const instance = instances.get(sectionId);
  if (instance) {
    instance.destroy();
    instances.delete(sectionId);
  }
}

function initAllSections() {
  document.querySelectorAll(SECTION_SELECTOR).forEach((section) => initSection(section));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSections);
} else {
  initAllSections();
}

document.addEventListener('shopify:section:load', (event) => {
  const container = event.target;
  if (!container) return;
  container.querySelectorAll(SECTION_SELECTOR).forEach((section) => initSection(section));
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!event.detail || !event.detail.sectionId) return;
  destroySection(event.detail.sectionId);
});
